@use '@/styles/tokens' as *;

/* Header cluster (rendered inside the drawer's cream title band) — Figma 429:301749 */
.header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
}

/* Figma 429:301750: 32px round badge, Accent 1/accent-900 */
.iconBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-pill);
  background: $color-accent1-900;
  color: var(--text-inverse);
}

.headerText {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
}

/* Figma 429:301753: Geist 18/Medium */
.headerHeading {
  font-family: $font-family-base;
  /* No 18px token (the scale jumps 16 → 20); Figma 429:301753 is explicit. */
  font-size: 18px;
  font-weight: $font-weight-medium;
  letter-spacing: -0.02em;
  color: $color-neutral-900;
}

/* Figma 429:301754: Order ID + status chip side by side */
.headerSubtitleRow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

/* Figma 429:301755: Geist 14/R, Text/Primary — rendered bare (Figma's `#` prefix is placeholder) */
.headerSubtitle {
  font-size: $font-size-md;
  color: $color-neutral-950;
}

/* Claim metadata card (Figma 429:301674): 2-column grid with per-row dividers, 5th cell full-width
   (Figma drew Address there; City occupies it — no endpoint in this contract carries an address) */
.infoCard {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--space-5);
  row-gap: var(--space-4);
  margin: 0 0 var(--space-5);
  padding: var(--space-5);
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
}

.infoItem {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;

  /* Left-column cells (1st, 3rd) carry the divider to the right column (Figma 429:301681). */
  &:nth-child(odd) {
    padding-inline-end: var(--space-5);
    border-inline-end: 1px solid $color-neutral-200;
  }
}

/* The 5th cell spans both columns and drops its right divider. */
.infoItemFull {
  grid-column: 1 / -1;
}
.infoItem.infoItemFull {
  padding-inline-end: 0;
  border-inline-end: none;
}

/* Figma 429:301677: Geist 13/R, Text/Muted */
.infoLabel {
  color: $color-neutral-600;
  font-size: $font-size-sm;
}

/* Figma 429:301680 / 301703: Geist 14/R, Text/Primary */
.infoValue {
  color: $color-neutral-950;
  font-size: $font-size-md;
}

/* Figma 429:301689 / 301695: the two date values sit one step back (Text/Secondary) */
.infoValueSecondary {
  color: $color-neutral-800;
  font-size: $font-size-md;
}

/* Reason + description card (Figma 429:301704): white, subtle border */
.reasonCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0 0 var(--space-5);
  padding: var(--space-5) var(--space-4);
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
}

/* Figma 429:301708: Geist 15/Medium, Text/Tertiary */
.reasonHeading {
  margin: 0;
  color: $color-neutral-700;
  /* No 15px token (the scale jumps 14 → 16); Figma 429:301708 is explicit. */
  font-size: 15px;
  font-weight: $font-weight-medium;
}

/* Figma 429:301711: Geist 14/R, Text/Tertiary */
.description {
  margin: 0;
  color: $color-neutral-700;
  font-size: $font-size-md;
  line-height: 1.5;
  overflow-wrap: break-word;
}

/* Device row (Figma 429:301716): name/category left, warranty-status chip right */
.deviceRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin: 0 0 var(--space-4);
}

.deviceInfo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* Figma 429:301719: Geist 16/Medium, Text/Primary */
.deviceName {
  overflow: hidden;
  color: $color-neutral-950;
  font-size: $font-size-lg;
  font-weight: $font-weight-medium;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Figma 429:301720: Geist 12/R, Text/Muted */
.deviceSubtitle {
  color: $color-neutral-600;
  font-size: $font-size-xs;
}

/* Attachments gallery (Figma 429:301722): 84px tiles, click → lightbox */
.thumbGrid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin: 0 0 var(--space-5);
  padding: 0;
  list-style: none;
}
.thumbBtn {
  width: 84px;
  height: 84px;
  padding: 0;
  border: none;
  border-radius: var(--radius-lg);
  background: var(--surface-subtle);
  overflow: hidden;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}
.thumbImg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Activity Timeline heading (Figma 429:301740): Geist 14/R, muted */
.timelineHeading {
  margin: var(--space-2) 0 var(--space-6);
  color: $color-neutral-600;
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
}

/* Rejection Reason (Figma 429:313417) — below the timeline, Rejected only */
.rejection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: var(--space-5) 0 0;
  padding: var(--space-3);
  background: $color-error-50;
  border-radius: var(--radius-xl);
}

/* Figma 429:313420: X glyph + Geist 14/R in the attention red */
.rejectionHeading {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: $color-stage-attention-text;
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
}

/* Figma 429:313421: Geist 12/R, Error/950 */
.rejectionValue {
  margin: 0;
  color: $color-error-950;
  font-size: $font-size-xs;
  font-weight: $font-weight-regular;
  overflow-wrap: break-word;
}

/* Attachment lightbox (Figma 413:275919): a centered bounded card over a dim scrim */
.lightboxMask {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: rgba($color-neutral-950, 55%);
}
.lightboxBackdrop {
  position: absolute;
  inset: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.lightboxCard {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: min(720px, 90vw);
  max-height: 85vh;
  padding: var(--space-5);
  background: var(--surface-subtle);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-overlay);
}
/* Figma 413:275922: dark round dismiss pill, Neutral/950 bg + inverse X */
.lightboxClose {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: var(--space-1);
  border: none;
  border-radius: var(--radius-xl);
  background: $color-neutral-950;
  color: var(--text-inverse);
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}
.lightboxImg {
  display: block;
  max-width: 100%;
  max-height: calc(85vh - var(--space-6) * 2);
  object-fit: contain;
}
